import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext, UserContext } from 'src/store/ContextStore';
import { suggestConversion } from 'src/util/suggestConversion';
import { getColor } from 'src/util/util';

/**
 * Opt-in conversion suggestion (TGCLOUD-1391, §6.2/§6.3).
 *
 * A gentle, dismissible card shown below a Process on open/edit. It SUGGESTS a
 * default management option (Tag or Pathway) but never converts on its own — a
 * conversion happens only on an explicit user click. The original Process is
 * always retained.
 *
 * Constraints honoured here:
 *  - "Try as a Pathway" calls the EXISTING pathway placement endpoint
 *    (POST /pathways/instances); it never auto-creates a template and routes
 *    through live-template selection.
 *  - "Try as a Tag" attaches a person-level tag to the member the Process is on
 *    (POST /pedigree/person-tags in append mode); the tag label defaults to the
 *    Process Type. The original Process is retained, not edited.
 *  - Dismissal ("Don't suggest again") persists per user + processType. It uses
 *    localStorage so no new backend store is introduced; whether this should be
 *    per-user or per-user-group server-side is an open question (Q#2) for a
 *    follow-up.
 */
function dismissalKey(userId, processType) {
  return `tgc:process-conversion-dismissed:${userId || 'anon'}:${(
    processType || ''
  )
    .trim()
    .toLowerCase()}`;
}

export default function ConversionSuggestionCard({
  process,
  probandUserId,
  customerDefaultMap,
  data,
  user
}) {
  const { setSnack } = React.useContext(SnackContext);
  const { user: loggedInUser } = React.useContext(UserContext);

  const processType = process?.processType?.trim();
  const userId = loggedInUser?._id;

  // Person identity for the tag conversion — the tag attaches to the member the
  // Process is on (mirrors the PersonTags save path).
  const familyMemberId =
    process?.familyMemberId ?? data?.familyMemberId ?? data?.proBandId;
  const isProband = !!data?.proBandId;
  const tagUserId = user?.user_id || user?._id;

  const [dismissed, setDismissed] = React.useState(false);
  const [showPathwayPicker, setShowPathwayPicker] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [assigning, setAssigning] = React.useState(false);
  const [showTagConfirm, setShowTagConfirm] = React.useState(false);
  const [tagging, setTagging] = React.useState(false);
  const [convertedTo, setConvertedTo] = React.useState(null);

  // Respect a previously persisted "don't suggest again" for this type.
  React.useEffect(() => {
    if (!processType) return;
    try {
      if (localStorage.getItem(dismissalKey(userId, processType)) === '1') {
        setDismissed(true);
      }
    } catch {
      // localStorage unavailable (private mode etc.) — just show the card.
    }
  }, [userId, processType]);

  const suggestion = React.useMemo(() => {
    if (!processType) return null;
    return suggestConversion({
      processType,
      processStatus: process?.processStatus,
      processStartDate: process?.processStartDate,
      endDate: process?.endDate,
      processComment: process?.processComment,
      customerDefaultMap
    });
  }, [processType, process, customerDefaultMap]);

  const openPathwayPicker = () => {
    setShowPathwayPicker(true);
    if (templates.length || loadingTemplates) return;
    setLoadingTemplates(true);
    axios
      .get(APICONSTANTS.pathwaysTemplates)
      .then(({ data: res }) => {
        const all = res?.templates || res || [];
        setTemplates(all.filter((t) => t.status === 'live'));
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'Unable to load pathways',
          severity: 'error'
        });
      })
      .finally(() => setLoadingTemplates(false));
  };

  // Calls the EXISTING pathway placement path. No core pathway logic is added.
  const handleAssignPathway = () => {
    if (!selectedTemplateId || !probandUserId) return;
    setAssigning(true);
    axios
      .post(APICONSTANTS.pathwayInstances, {
        pathwayTemplateId: selectedTemplateId,
        probandUserId
      })
      .then(() => {
        setSnack({
          status: true,
          msg: 'Added to the pathway. This Process is kept as-is.',
          severity: 'success'
        });
        setConvertedTo('pathway');
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err?.response?.data?.message || 'Could not add to pathway',
          severity: 'error'
        });
      })
      .finally(() => setAssigning(false));
  };

  // Calls the EXISTING person-tags path in append mode — attaches one tag to the
  // member without touching their other person tags, and never edits the Process.
  const handleAddAsTag = () => {
    if (!processType || !familyMemberId || !tagUserId) return;
    setTagging(true);
    const tagColor = getColor(processType);
    axios
      .post(APICONSTANTS.personTags, {
        user_id: tagUserId,
        familyMemberId,
        isProband,
        personTags: [{ tag: processType, tagColor }],
        clientTags: [{ label: processType, tagColor }],
        append: true
      })
      .then(() => {
        setSnack({
          status: true,
          msg: 'Added as a tag. This Process is kept as-is.',
          severity: 'success'
        });
        setConvertedTo('tag');
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err?.response?.data?.message || 'Could not add as a tag',
          severity: 'error'
        });
      })
      .finally(() => setTagging(false));
  };

  const handleDontSuggestAgain = () => {
    try {
      localStorage.setItem(dismissalKey(userId, processType), '1');
    } catch {
      // ignore — fall back to a session-only dismissal
    }
    setDismissed(true);
  };

  if (!suggestion || dismissed) return null;

  const highlightTag = suggestion.suggested === 'tag';
  const highlightPathway = suggestion.suggested === 'pathway';

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Paper
        variant="outlined"
        sx={{ p: 2, borderColor: 'secondary.main', bgcolor: 'action.hover' }}
      >
        <Stack spacing={1.5}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700
            }}
          >
            There may be an easier way to manage this list
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            {suggestion.rationale} You can keep this as a Process — this is just
            a suggestion, and nothing changes unless you choose an option below.
          </Typography>

          {convertedTo ? (
            <Typography
              variant="body2"
              sx={{
                color: 'success.main'
              }}
            >
              {convertedTo === 'tag'
                ? 'Done — added as a tag. The original Process is unchanged.'
                : 'Done — added to the pathway. The original Process is unchanged.'}
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  type="button"
                  size="small"
                  startIcon={<LocalOfferOutlinedIcon />}
                  variant={highlightTag ? 'contained' : 'outlined'}
                  onClick={() => setShowTagConfirm(true)}
                >
                  Try as a Tag
                </Button>

                <Button
                  type="button"
                  size="small"
                  startIcon={<AltRouteIcon />}
                  variant={highlightPathway ? 'contained' : 'outlined'}
                  onClick={openPathwayPicker}
                >
                  Try as a Pathway
                </Button>

                <Button
                  type="button"
                  size="small"
                  variant="text"
                  onClick={() => setDismissed(true)}
                >
                  Keep as a Process
                </Button>
              </Box>

              {showTagConfirm && (
                <Box sx={{ pt: 0.5 }}>
                  {!familyMemberId || !tagUserId ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      This record isn’t linked to a person, so it can’t be
                      tagged.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        This will add the tag “{processType}” to this person.
                        The Process is kept as-is.
                      </Typography>
                      <Box>
                        <Button
                          type="button"
                          size="small"
                          variant="contained"
                          disabled={tagging}
                          startIcon={
                            tagging ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : null
                          }
                          onClick={handleAddAsTag}
                        >
                          {tagging ? 'Adding…' : 'Add tag'}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>
              )}

              {showPathwayPicker && (
                <Box sx={{ pt: 0.5 }}>
                  {!probandUserId ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      Link this node to a registered patient to use Pathways.
                    </Typography>
                  ) : loadingTemplates ? (
                    <CircularProgress size={18} />
                  ) : templates.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      No live pathway templates are available to choose from.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="process-pathway-label">
                          Select Pathway
                        </InputLabel>
                        <Select
                          labelId="process-pathway-label"
                          label="Select Pathway"
                          value={selectedTemplateId}
                          onChange={(e) =>
                            setSelectedTemplateId(e.target.value)
                          }
                        >
                          {templates.map((t) => (
                            <MenuItem key={t._id} value={t._id}>
                              {t.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box>
                        <Button
                          type="button"
                          size="small"
                          variant="contained"
                          disabled={!selectedTemplateId || assigning}
                          startIcon={
                            assigning ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : null
                          }
                          onClick={handleAssignPathway}
                        >
                          {assigning ? 'Adding…' : 'Add to this pathway'}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>
              )}

              <Box>
                <Button
                  type="button"
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={handleDontSuggestAgain}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Don’t suggest this for “{processType}” again
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
